import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { verificationApi } from './api'
import type {
  SubjectType,
  VerifyFieldRequest,
  RevokeVerificationRequest,
} from './types'

export const verificationKeys = {
  all: ['field-verifications'] as const,
  bySubject: (subjectType: SubjectType, subjectId: number) =>
    [...verificationKeys.all, subjectType, subjectId] as const,
  detail: (id: number) => [...verificationKeys.all, 'detail', id] as const,
}

export function useFieldVerifications(
  subjectType: SubjectType, subjectId: number | undefined,
) {
  return useQuery({
    queryKey: verificationKeys.bySubject(subjectType, subjectId ?? 0),
    queryFn: () => verificationApi.listBySubject(subjectType, subjectId!),
    enabled: subjectId !== undefined && subjectId > 0,
  })
}

export function useFieldVerificationDetail(id: number | undefined) {
  return useQuery({
    queryKey: verificationKeys.detail(id ?? 0),
    queryFn: () => verificationApi.get(id!),
    enabled: id !== undefined && id > 0,
  })
}

export function useVerifyField(subjectType: SubjectType, subjectId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: VerifyFieldRequest) => verificationApi.verify(body),
    onSuccess: () => qc.invalidateQueries({
      queryKey: verificationKeys.bySubject(subjectType, subjectId),
    }),
  })
}

export function useRevokeFieldVerification(subjectType: SubjectType, subjectId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: RevokeVerificationRequest }) =>
      verificationApi.revoke(id, body),
    onSuccess: () => qc.invalidateQueries({
      queryKey: verificationKeys.bySubject(subjectType, subjectId),
    }),
  })
}
